<template>
	<v-menu
		activator=".dataobject-context-menu"
		:model-value="compProps.show"
		:open-on-click="false"
		:open-on-focus="false"
		:open-on-hover="false"
		:close-on-back="true"
		:close-on-content-click="true"
	>
		<v-list>
			<v-list-item
				:disabled="!domainStore.hasAccessRights(UserRole.Editor) || !cardinalityAllowsRemoval"
				prepend-icon="mdi-minus"
				block
				@click="removeEntry"
			>
				{{ symbolsStore.GUILabel("3cd68c08-7bc1-43b7-b38f-aed2dba9795e") }}
			</v-list-item>
		</v-list>
	</v-menu>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import * as d3 from "d3";
import { useDomainStore } from "../../../store/domain";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useGraphStore } from "../../../store/graph";
import { useSymbolsStore } from "../../../store/symbols";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";

const domainStore = useDomainStore();
const workspacesStore = useInstanceWorkspacesStore();
const graphStore = useGraphStore();
const symbolsStore = useSymbolsStore();

const compProps = defineProps<{
	dataobjectId: string;
	show: boolean;
}>();
const emits = defineEmits(["remove"]);

const instance = computed(() => {
	if (!workspacesStore.openedWorkspace) return;
	return graphStore.instance.dataobject.byId(compProps.dataobjectId);
});
const template = computed(() => {
	if (!workspacesStore.openedWorkspace) return;
	if (!instance.value) return;
	return graphStore.template.dataobject.byId(instance.value.template);
});

const cardinalityAllowsRemoval = computed(() => {
	if (!workspacesStore.openedWorkspace) return false;
	if (!instance.value || !template.value) return false;
	if (isRootDataobject()) return false;
	if (
		!template.value.cardinality?.min ||
		template.value.cardinality?.min === 0 ||
		template.value.cardinality?.min === -1
	)
		return true;

	const countInstances = workspacesStore.workspaceGraph.nodes
		.map((n) => graphStore.instance.dataobject.byId(n.id))
		.filter((d) => d)
		.filter((d) => d?.template === template.value?.id).length;

	return (
		(template.value.cardinality?.min &&
			(template.value.cardinality.min === 0 || countInstances > template.value.cardinality?.min)) ||
		false
	);
});

function isRootDataobject() {
	if (!workspacesStore.openedWorkspace) return false;
	return workspacesStore.openedWorkspace.dataobjects.includes(compProps.dataobjectId);
}

function removeEntry() {
	if (!workspacesStore.openedWorkspace) return;
	let instanceDataobject = graphStore.instance.dataobject.byId(compProps.dataobjectId);
	if (instanceDataobject) {
		graphStore.instance.dataobject.remove(instanceDataobject.id);
	}
	emits("remove");
}

watch(
	() => compProps.show,
	() => {
		if (compProps.show) {
			const g = d3.select(`g[data-id="${compProps.dataobjectId}"]`);
			g.classed("dataobject-context-menu", true);
		} else {
			d3.selectAll(".dataobject-context-menu").classed("dataobject-context-menu", false);
		}
	}
);
</script>

<style scoped lang="scss"></style>
